/* eslint-disable no-param-reassign */
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import mapboxgl, { Map, PaintSpecification, LayoutSpecification } from "@iventis/mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { createGoogleMapSession } from "@iventis/google/src/google-map-background-session";
import { cadFillLayer, cadLineLayer, cadSourceId, cadTextLayer } from "./mapbox-georeferencer-layers";
import { mapboxKey } from "../api-keys";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { CadStyle } from "../types/style";
import { blankMapBackgrounds, MapBackground } from "../types/map-background";
import { getBackgroundStyle, mapRequestInterceptor } from "./mapbox-cad-editor-helpers";
import { ICadApiContext } from "../context/cad-api-context";
import { CadApiConfig } from "../types/cad-api-config";

mapboxgl.accessToken = mapboxKey;

export class MapboxCadGeoEditor {
    /** Mapbox instance which is the real world map */
    private readonly backgroundMap: Map;

    private readonly backgroundMapDraw: MapboxDraw;

    private googleMapsUrl: string;

    private subscriptions: Subscription[] = [];

    private getGoogleMapBackgroundApiKey: () => Promise<{ apiKey: string; session: string }>;

    private cadApiContext: ICadApiContext;

    public projecting$ = new BehaviorSubject<boolean>(false);

    private projectionAbortController: AbortController | null = null;

    constructor(
        backgroundMapElement: HTMLDivElement,
        onLoaded: () => void,
        mapApiKeys: { getGoogleMapBackgroundApiKey: () => Promise<{ apiKey: string; session: string }>; osMapsApiKey: string },
        cadApiContext: ICadApiContext
    ) {
        this.backgroundMapDraw = this.createBackgroundMapDraw();
        this.backgroundMap = this.createMapbackgroundMap(backgroundMapElement, onLoaded);
        this.getGoogleMapBackgroundApiKey = mapApiKeys.getGoogleMapBackgroundApiKey;
        this.cadApiContext = cadApiContext;
    }

    private createBackgroundMapDraw() {
        const draw = new MapboxDraw({
            displayControlsDefault: false,
        });
        return draw;
    }

    private createMapbackgroundMap(element: HTMLDivElement, onLoaded: () => void) {
        const map = new Map({
            container: element,
            center: [50, 50],
            zoom: 2,
            projection: "mercator",
            style: "mapbox://styles/mapbox/standard",
            maxPitch: 0,
            maxZoom: 24,
            bearingSnap: 0,
            logoPosition: "bottom-right",
            transformRequest: (url) => mapRequestInterceptor(url),
        });
        map.doubleClickZoom.disable();
        map.once("style.load", () => {
            map.addControl(this.backgroundMapDraw);
        });
        map.once("idle", () => onLoaded());
        return map;
    }

    public startDraw() {
        this.backgroundMapDraw.deleteAll();
        this.backgroundMapDraw.changeMode("draw_polygon");
    }

    public onDrawEnd() {
        return new Observable((observer) => {
            const handler = (event) => {
                observer.next(event.features[0]);
            };

            this.backgroundMap.on("draw.create", handler);
            this.backgroundMap.on("draw.update", handler);

            return () => {
                this.backgroundMap.off("draw.create", handler);
                this.backgroundMap.off("draw.update", handler);
            };
        });
    }

    public onCadClicked() {
        return new Observable((observer) => {
            const handler = (event) => {
                const features = this.backgroundMap.queryRenderedFeatures(event.point, {
                    layers: [cadLineLayer.id, cadFillLayer.id],
                });
                if (features.length === 0) {
                    return;
                }
                observer.next(features);
            };
            this.backgroundMap.on("click", handler);
            return () => {
                this.backgroundMap.off("click", handler);
            };
        });
    }

    public async project(cadClip: number[][], style: CadStyle) {
        // Cancel any ongoing projection polling
        if (this.projectionAbortController) {
            this.projectionAbortController.abort();
        }
        this.projectionAbortController = new AbortController();
        const { signal } = this.projectionAbortController;
        // clear any existing cad source
        const existingSource = this.backgroundMap.getSource(cadSourceId);
        if (existingSource) {
            this.backgroundMap.removeSource(cadSourceId);
        }
        this.projecting$.next(true);
        try {
            // PUT config to API
            await this.cadApiContext.updateConfig({
                cadClip,
                geoReferenced: true,
                rotation: 0,
                origin: [],
            });

            // Poll every 1s until projecting is false using a recursive async function with cancellation
            let config: CadApiConfig | null = null;
            const pollProjection = async (): Promise<void> => {
                if (signal.aborted) return;
                await new Promise((res) => setTimeout(res, 1000));
                if (signal.aborted) return;
                try {
                    config = await this.cadApiContext.getConfig(); // Adjust URL as needed
                    if (config.state === "projecting") {
                        await pollProjection();
                    }
                } catch (e) {
                    if (signal.aborted) return;
                    await pollProjection();
                }
            };
            await pollProjection();

            if (signal.aborted) return;
            // Add or update vector tile source
            this.addCadSource();
            this.fitToBounds([config.projectedBounds.maxX, config.projectedBounds.maxY, config.projectedBounds.minX, config.projectedBounds.minY]);

            const cadMapStyle = this.backgroundMap.getStyle();

            if (cadMapStyle == null) {
                throw new Error("Cad map style is null");
            }

            if (!cadMapStyle.layers.find((l) => l.source === cadSourceId)) {
                this.addCadLayers();
            }
            this.updateCadStyle(style);
            this.projecting$.next(false);
        } catch (error) {
            this.projecting$.next(false);
            throw error;
        }
    }

    public updateCadStyle(style: CadStyle) {
        const cadStyleTextLayer = style.layers.find((l) => l.type === "symbol");

        if (!cadStyleTextLayer) {
            return; // Do nothing if no text layer is found
        }

        // Check if the layer exists in the map and update it
        if (this.backgroundMap.getLayer(cadTextLayer.id)) {
            // Apply the changes to the existing layer in the map
            Object.entries(cadStyleTextLayer.paint || {}).forEach(([key, value]) => {
                this.backgroundMap.setPaintProperty(cadTextLayer.id, key as keyof PaintSpecification, value);
            });

            Object.entries(cadStyleTextLayer.layout || {}).forEach(([key, value]) => {
                this.backgroundMap.setLayoutProperty(cadTextLayer.id, key as keyof LayoutSpecification, value);
            });
        }
    }

    public async changeBackground(selectedMapBackground: MapBackground, cadStyle: CadStyle, layerIdsToExclude: string[]) {
        if (blankMapBackgrounds.includes(selectedMapBackground.name.toLowerCase())) {
            this.backgroundMap.setStyle({
                version: 8,
                glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
                sources: {},
                layers: [
                    {
                        id: "background",
                        type: "background",
                        paint: {
                            "background-color": "#ffffff",
                        },
                    },
                ],
            });
        } else {
            const style = await getBackgroundStyle(selectedMapBackground.url);
            if (style != null) {
                if (typeof style === "object" && style.sources.google) {
                    const googleSource = style.sources.google;
                    const apiKey =
                        "tiles" in googleSource && Array.isArray(googleSource.tiles)
                            ? googleSource.tiles[0]?.match(/key=([^&]*)/)?.[1]
                            : "url" in googleSource && typeof googleSource.url === "string"
                            ? googleSource.url.match(/key=([^&]*)/)?.[1]
                            : undefined;
                    const sessionToken = await createGoogleMapSession(apiKey);
                    if (googleSource && "tiles" in googleSource && Array.isArray(googleSource.tiles)) {
                        googleSource.tiles = googleSource.tiles.map((t) => t.replace("{session}", sessionToken));
                    }
                    this.backgroundMap.setStyle(style);
                } else {
                    this.backgroundMap.setStyle(selectedMapBackground.url);
                }
            }
        }

        this.backgroundMap.once("idle", () => {
            this.backgroundMap.getStyle().layers.forEach((layer) => {
                // Hide or show all 3d areas in the map background
                if (layer != null && layer.type === "fill-extrusion") {
                    this.backgroundMap.setLayoutProperty(layer.id, "visibility", "none");
                }

                // Hide or show 2d areas in their place
                if (layer != null && layer.type === "fill") {
                    this.backgroundMap.setLayerZoomRange(layer.id, layer.minzoom ?? 0, 24);
                }
            });

            this.addCadSource();
            this.addCadLayers();
            this.updateCadStyle(cadStyle);
            this.backgroundMap.once("idle", () => {
                this.filterCadLayers(layerIdsToExclude);
            });
        });
    }

    public addCadSource() {
        const existingSource = this.backgroundMap.getSource(cadSourceId);
        const tileUrl = `${this.cadApiContext.apiUrl}tiles/{z}/{x}/{y}.pbf`;
        if (!existingSource) {
            this.backgroundMap.addSource(cadSourceId, {
                type: "vector",
                tiles: [tileUrl],
                minzoom: 0,
                maxzoom: 22,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (typeof (existingSource as any).setTiles === "function") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (existingSource as any).setTiles([tileUrl]);
        }
    }

    public fitToBounds(bounds: [number, number, number, number]) {
        this.backgroundMap.fitBounds(bounds, { duration: 0, animate: false });
    }

    public removeDrawings() {
        this.backgroundMapDraw.deleteAll();
    }

    public filterCadLayers(layerIds: string[]) {
        if (!this.backgroundMap.isStyleLoaded()) {
            return;
        }
        this.backgroundMap.setFilter(cadFillLayer.id, ["all", ["!in", "eLayer", ...layerIds], ["==", "$type", "Polygon"]]);
        this.backgroundMap.setFilter(cadLineLayer.id, ["all", ["!in", "eLayer", ...layerIds], ["==", "$type", "LineString"]]);
        this.backgroundMap.setFilter(cadTextLayer.id, ["all", ["!in", "eLayer", ...layerIds], ["==", "$type", "Point"]]);
    }

    public resizeMap() {
        this.backgroundMap.resize();
    }

    private addCadLayers() {
        this.backgroundMap.addLayer(cadLineLayer);
        this.backgroundMap.addLayer(cadFillLayer);
        this.backgroundMap.addLayer(cadTextLayer);
    }

    public destroy() {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
    }
}
